import { forkJoin, Observable, of, Subject } from 'rxjs';
import { FormArray, FormGroup } from '@angular/forms';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { CANDIDATES_STATUS_PARAM, DEFAULT_PAGINATION_PARAMS, DESCENDING_SORT } from '@app/lib/constants/http.constants';
import { EnvironmentService } from '@app/environments/services/environment.service';
import { getQualityScore, getResearchStudyDisplay, sortByDateFunction } from 'projects/labcorp/clinical-trials-ui/src/lib/utils';
import { ICandidate } from '../interfaces';
import { ICandidatesState } from '../state/candidates.reducer';
import { IFilterListOption, IFilterParam, IHttpParams, IListNavigator } from '@app/lib/interfaces';
import { INELIGIBLE } from '../candidate-details.constants';
import { IResearchStudy, IResearchStudyDisplay, IResearchSubject } from 'projects/labcorp/clinical-trials-ui/src/lib/interfaces';
import { IResourceStatus } from 'projects/labcorp/clinical-trials-ui/src/lib/types';
import { LAST_UPDATED } from '@app/lib/constants/fhir.constant';
import { NotApplicablePipe } from '@app/lib/pipes/not-applicable.pipe';
import * as candidatesSelectors from '../state/candidates.selectors';
import * as CandidateActions from '../state/candidates.actions';
import * as utils from '@app/lib/utils';
import * as progressUtils from '@app/portal/candidates/candidate-progress.utils';

@Injectable({
  providedIn: 'root'
})
export class CandidatesService {

  apiUrl: string;
  cancelCandidateDetailChanges = new Subject();
  cancelCandidateDetailChanges$: Observable<unknown>;
  candidates$: Observable<ICandidate[]>;
  candidateCriteria$: Observable<fhir4.QuestionnaireResponse[]>;
  candidatesToArchiveOrUnarchive = new Subject<{ candidates: ICandidate[], status: IResourceStatus }>();
  candidatesToArchiveOrUnarchive$: Observable<{ candidates: ICandidate[], status: IResourceStatus }>;
  candidatesToUpdate = new Subject<{ candidates: ICandidate[], status: string }>();
  candidatesToUpdate$: Observable<{ candidates: ICandidate[], status: string }>;
  candidateUpdateSuccess = new Subject<number>();
  candidateUpdateSuccess$: Observable<number>;
  candidateListNavigator$: Observable<IListNavigator>;
  notApplicable = new NotApplicablePipe();
  organizations$: Observable<fhir4.Organization[]>;
  organizationsProcessing$: Observable<boolean>;
  researchStudies$: Observable<IResearchStudyDisplay[]>;
  researchStudiesProcessing$: Observable<boolean>;
  selectedCandidate$: Observable<ICandidate>;
  selectedOrganization$: Observable<fhir4.Organization>;
  selectedResearchStudy$: Observable<IResearchStudyDisplay>;
  viewArchivedCandidatesList$: Observable<boolean>;

  constructor(
    private readonly environmentService: EnvironmentService,
    private readonly httpClient: HttpClient,
    private readonly store: Store<ICandidatesState>,
  ) {
    this.apiUrl = this.environmentService.environment.restAPI;
    this.cancelCandidateDetailChanges$ = this.cancelCandidateDetailChanges.asObservable();
    this.candidates$ = this.store.select(candidatesSelectors.candidates);
    this.candidatesToArchiveOrUnarchive$ = this.candidatesToArchiveOrUnarchive.asObservable();
    this.candidatesToUpdate$ = this.candidatesToUpdate.asObservable();
    this.candidateUpdateSuccess$ = this.candidateUpdateSuccess.asObservable();
    this.candidateListNavigator$ = this.store.select(candidatesSelectors.candidateListNavigator);
    this.organizations$ = this.store.select(candidatesSelectors.organizations);
    this.organizationsProcessing$ = this.store.select(candidatesSelectors.organizationsProcessing);
    this.researchStudies$ = this.store.select(candidatesSelectors.researchStudies);
    this.researchStudiesProcessing$ = this.store.select(candidatesSelectors.researchStudiesProcessing);
    this.selectedCandidate$ = this.store.select(candidatesSelectors.selectedCandidate);
    this.selectedOrganization$ = this.store.select(candidatesSelectors.selectedOrganization);
    this.selectedResearchStudy$ = this.store.select(candidatesSelectors.selectedResearchStudy);
    this.viewArchivedCandidatesList$ = this.store.select(candidatesSelectors.viewArchivedCandidates);
  }

  getOrganizations(): void {
    this.store.dispatch(CandidateActions.getOrganizations());
  }

  _getOrganizations(email: string): Observable<fhir4.Organization[]> {
    return this.httpClient
                .get<fhir4.Bundle>(`${ this.apiUrl }/PractitionerRole?email=${ email }`)
                .pipe(
                  map((bundle: fhir4.Bundle) => {
                    return bundle.entry?.filter(entry => entry.resource.resourceType === 'Organization').map(entry => entry.resource) as fhir4.Organization[] || [];
                  }),
                );
  }

  selectOrganization(organization: fhir4.Organization): void {
    this.store.dispatch(CandidateActions.selectOrganization({ organization }));
  }

  getResearchStudies(): void {
    this.store.dispatch(CandidateActions.getResearchStudies());
  }

  _getResearchStudies(id: string): Observable<IResearchStudyDisplay[]> {
    const params: HttpParams = utils.objectToHttpParams<IHttpParams>(CANDIDATES_STATUS_PARAM);

    return this.httpClient
                .get<fhir4.Bundle>(`${ this.apiUrl }/ResearchStudy?associatedParty=Organization/${ id }`, { params })
                .pipe(
                  map((bundle: fhir4.Bundle) => {
                    if (bundle.entry) {
                      const studies: IResearchStudyDisplay[] = bundle.entry.filter(entry => entry.resource.resourceType === 'ResearchStudy').map(entry => {
                        const researchStudy: IResearchStudy = entry.resource as IResearchStudy;
                        const study = getResearchStudyDisplay(researchStudy);

                        return study;
                      });

                      return studies;
                    }

                    return [];
                  }));
  }

  selectResearchStudy(researchStudy: IResearchStudyDisplay): void {
    this.store.dispatch(CandidateActions.selectResearchStudy({ researchStudy }));
  }

  getCandidates(): void {
    this.store.dispatch(CandidateActions.getCandidates());
  }

  _getPaginatedCandidates(orgID: string, study: IResearchStudyDisplay, params: IHttpParams): Observable<ICandidate[]> {

    return this._getResearchSubjects(orgID, study, params)
                .pipe(
                  map((bundle: fhir4.Bundle) => {
                    if (bundle.entry) {
                      const candidates: ICandidate[] = bundle.entry.map(entry => {
                        if (entry.resource.resourceType === 'ResearchSubject') {
                          const researchSubject: IResearchSubject = entry.resource as unknown as IResearchSubject;
                          const candidateStatus = utils.getCandidateStatus(researchSubject.progress);
                          const candidate: ICandidate = {
                            candidateStatus,
                            dateAdded: utils.getDateAdded(researchSubject.progress),
                            eligible: candidateStatus !== INELIGIBLE,
                            qualityScore: getQualityScore(researchSubject.extension),
                            researchSubject,
                            study: this.notApplicable.transform(study.researchStudy.title),
                          };

                          const candidateID: string = researchSubject.subject.reference.split('/')[1];

                          const patient: fhir4.Patient = bundle.entry.find(obj => obj.resource.id === candidateID).resource as fhir4.Patient;
                          candidate.patient = {
                            address: utils.getAddress(patient.address),
                            birthdate: utils.formatDateString(patient.birthDate),
                            email: utils.getEmail(patient.telecom),
                            ethnicity: utils.getEthnicity(patient.extension),
                            fullName: utils.getName(patient.name),
                            gender: this.notApplicable.transform(patient.gender),
                            homePhone: utils.getPhoneNumber(patient.telecom, 'home'),
                            mobilePhone: utils.getPhoneNumber(patient.telecom, 'mobile'),
                            race: utils.getRace(patient.extension)
                          };

                          return candidate;
                        }

                        return null;
                      }).filter(subject => Boolean(subject));
                      return candidates;
                    }
                    return [];
                  })
                );
  }

  _getCandidatesTotal(orgID: string, study: IResearchStudyDisplay, params: IHttpParams): Observable<number> {
    return this._getResearchSubjects(orgID, study, params)
                .pipe(
                  map((bundle: fhir4.Bundle) => {
                    return bundle.total;
                  })
                );
  }

  _getCandidates(orgID: string, study: IResearchStudyDisplay, params: IHttpParams): Observable<{ candidates: ICandidate[], total: number }> {
    const defaultParams: IHttpParams = {
      ...params,
      ...DEFAULT_PAGINATION_PARAMS,
    };

    const subscribeables = [
      this._getPaginatedCandidates(orgID, study, params),
      this._getCandidatesTotal(orgID, study, defaultParams)
    ];

    return forkJoin(subscribeables)
            .pipe(
              map(res => {
                const candidates: ICandidate[] = res[0] as ICandidate[];
                const total: number = res[1] as number;
                return {
                  candidates,
                  total
                };
              })
            );
  }

  _getResearchSubjects(orgID: string, study: IResearchStudyDisplay, params: IHttpParams): Observable<fhir4.Bundle> {
    const httpParams = utils.objectToHttpParams<IHttpParams>(params);

    return this.httpClient.get<fhir4.Bundle>(`${ this.apiUrl }/ResearchSubject?study=ResearchStudy/${ study.researchStudy.id }&patient.source-organization=Organization/${ orgID }`, { params: httpParams });
  }

  setCandidateListPageIndex(index: number): void {
    this.store.dispatch(CandidateActions.setPageIndex({ index }));
  }

  selectCandidate(candidate: ICandidate): void {
    this.store.dispatch(CandidateActions.selectCandidate({ candidate }));
  }

  _getQuestionnaireResponse(id: string): Observable<fhir4.QuestionnaireResponse> {
    const headers: HttpHeaders = new HttpHeaders({
      'Cache-Control': 'no-cache',
    });

    return this.httpClient
                .get<fhir4.Bundle>(`${ this.apiUrl }/QuestionnaireResponse?subject=ResearchSubject/${ id }&_sort=${ DESCENDING_SORT }${ LAST_UPDATED }`, { headers })
                .pipe(
                  map(bundle => {
                    const questionnaireResponses = bundle?.entry?.map(entry => entry.resource) as fhir4.QuestionnaireResponse[] || [];
                    return questionnaireResponses.sort((a, b) => {
                      return sortByDateFunction(a.meta?.lastUpdated, b.meta?.lastUpdated);
                    })[0];
                  })
                );
  }

  _getGroup(study: IResearchStudy): Observable<fhir4.Group> {
    return this.httpClient.get<fhir4.Group>(`${ this.apiUrl }/${ study.recruitment?.eligibility?.reference }`);
  }

  getCriteria(study: IResearchStudyDisplay, candidate: ICandidate): Observable<{group: fhir4.Group, questionnaireResponse: fhir4.QuestionnaireResponse }> {
    const subscribeables = [
      study.group ? of(study.group) : this._getGroup(study.researchStudy),
      candidate.questionnaireResponse ? of(candidate.questionnaireResponse) : this._getQuestionnaireResponse(candidate.researchSubject.id)
    ];

    return forkJoin(subscribeables)
            .pipe(
              map(res => {
                const group: fhir4.Group = res[0] as fhir4.Group;
                const questionnaireResponse: fhir4.QuestionnaireResponse = res[1] as fhir4.QuestionnaireResponse;
                return { group, questionnaireResponse };
              })
            );
  }

  clearAllSelected(): void {
    this.store.dispatch(CandidateActions.clearAllSelected());
  }

  clearSelectedCandidate(): void {
    this.store.dispatch(CandidateActions.clearSelectedCandidate());
  }

  onUpdateCandidateSuccess(unknownCriteria: number): void {
    this.candidateUpdateSuccess.next(unknownCriteria);
  }

  onCancelCandidateChanges(): void {
    this.cancelCandidateDetailChanges.next();
  }

  updateCandidateDetails(questionnaireResponseData: fhir4.QuestionnaireResponse, researchSubjectData: IResearchSubject): void {
    this.store.dispatch(CandidateActions.updateCandidateDetails({ questionnaireResponseData, researchSubjectData }));
  }

  _updateCandidateDetails(questionnaireResponse: fhir4.QuestionnaireResponse, researchSubject: IResearchSubject): Observable<{questionnaireResponse: fhir4.QuestionnaireResponse, researchSubject: IResearchSubject}> {
    const qualityScore = getQualityScore(researchSubject.extension);

    const subscribeables = [
      this._updateCandidateCriteria(questionnaireResponse),
      this._updateResearchSubject(researchSubject, researchSubject.progress),
      this._updateResearchSubject(researchSubject, {
        name: 'score',
        numerator: qualityScore.numerator.value,
        denominator: qualityScore.denominator.value
      }),
    ];

    return forkJoin(subscribeables)
           .pipe(
             map((res) => ({
               questionnaireResponse: res[0] as fhir4.QuestionnaireResponse,
               researchSubject: {
                 ...res[2] as IResearchSubject,
                 progress : (res[1] as IResearchSubject).progress,
                }
             }))
           );
  }

  _updateCandidateCriteria(questionnaireResponse: fhir4.QuestionnaireResponse): Observable<fhir4.QuestionnaireResponse> {
    return this.httpClient.post<fhir4.QuestionnaireResponse>(`${ this.apiUrl }/QuestionnaireResponse`, questionnaireResponse);
  }

  updateMultipleCandidateProgress(candidates: ICandidate[], statusValue: string): void {
    this.store.dispatch(CandidateActions.updateMultipleCandidateProgress({ candidates, statusValue }));
  }

  _updateMultipleCandidateProgress(candidates: ICandidate[], status: string): Observable<any> {
    const subscribeables = [];

    candidates.forEach(candidate => {
      const progressForm = new FormGroup({
        progress: progressUtils.buildProgressForm(candidate)
      });
      progressUtils.updateCandidateStatus((progressForm.get('progress') as FormArray), status);

      subscribeables.push(this._updateResearchSubject(candidate.researchSubject, progressForm.value['progress']));
    });

    return forkJoin(subscribeables)
           .pipe(
             map(res => res as IResearchSubject[])
           );
  }

  _updateResearchSubject(researchSubject: IResearchSubject, requestBody: any): Observable<IResearchSubject> {
    return this.httpClient.patch<IResearchSubject>(`${ this.apiUrl }/ResearchSubject/${ researchSubject.id }`, requestBody);
  }

  onArchiveOrUnarchive(candidates: ICandidate[], status: IResourceStatus): void {
    this.candidatesToArchiveOrUnarchive.next({ candidates, status });
  }

  onCandidateStatusUpdate(candidates: ICandidate[], status: string): void {
    this.candidatesToUpdate.next({
      candidates,
      status
    });
  }

  updateResearchSubjectStatus(status: IResourceStatus): void {
    this.store.dispatch(CandidateActions.updateResearchSubjectStatus({ status }));
  }

  updateMultipleResearchSubjectStatus(candidates: ICandidate[], status: IResourceStatus): void {
    this.store.dispatch(CandidateActions.updateMultipleResearchSubjectStatus({ candidates, status }));
  }

  _updateMultipleResearchSubjectStatus(candidates: ICandidate[], status: IResourceStatus): Observable<IResearchSubject[]> {
    const subscribeables = [];

    candidates.forEach(candidate => {
      subscribeables.push(this._updateResearchSubject(candidate.researchSubject, {
        name: 'status',
        value: status
      }));
    });

    return forkJoin(subscribeables)
           .pipe(
             map(res => res as IResearchSubject[])
           );
  }

  exportAllCandidates(): void {
    this.store.dispatch(CandidateActions.exportAllCandidates());
  }

  _exportAllCandidates(bundle: fhir4.Bundle): Observable<any> {
    return this.httpClient.post(`${ this.apiUrl }/ResearchSubject/csv-all`, bundle, { responseType: 'blob' });
  }

  exportMultipleCandidates(candidates: ICandidate[]): void {
    this.store.dispatch(CandidateActions.exportMultipleCandidates({ candidates }));
  }

  _exportMultipleCandidates(candidates: ICandidate[]): Observable<any> {
    const reqJson = {};

    candidates.forEach((candidate, index) => {
      reqJson[`${ index }`] = candidate.researchSubject.id;
    });

    return this.httpClient.post(`${ this.apiUrl }/ResearchSubject/csv`, reqJson, { responseType: 'blob' });
  }

  updateFilterParams(filterParam: IFilterParam): void {
    this.store.dispatch(CandidateActions.updateFilterParams({ filterParam }));
  }

  updateFilterListOptions(filterListOptions: IFilterListOption[]): void {
    this.store.dispatch(CandidateActions.updateFilterListOptions({ filterListOptions }));
  }

  updateCandidateEligibility(eligible: boolean): void {
    this.store.dispatch(CandidateActions.updateEligibility({ eligible }));
  }

  toggleViewArchivedCandidates(): void {
    this.store.dispatch(CandidateActions.toggleViewArchivedCandidates());
  }
}
